import { motion } from "framer-motion";
import { Building, MapPin, Flag, Plus } from "lucide-react";

const GRID = "grid sm:grid-cols-2 lg:grid-cols-3 gap-3";
const EMPTY = "bg-white border border-border rounded-xl p-6 text-center text-sm text-muted-foreground";

export function DormsView({ dorms, DistanceRow }) {
  if (!dorms || dorms.length === 0) {
    return <div className={EMPTY}>No on-campus residence halls added for this campus yet.</div>;
  }
  return (
    <div className={GRID} data-testid="dorms-view">
      {dorms.map((d) => (
        <motion.div key={d.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-border rounded-xl p-5" data-testid={`dorm-${d.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-brand/10 text-brand flex items-center justify-center">
              <Building size={16} strokeWidth={1.5} />
            </div>
            <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{d.area || d.style || "residence hall"}</span>
          </div>
          <h3 className="font-display text-lg font-semibold leading-tight">{d.name}</h3>
          <DistanceRow est={d._est} testid={`dorm-dist-${d.id}`} />
        </motion.div>
      ))}
    </div>
  );
}

export function ApartmentsView({ housing, hasData, openReport, DistanceRow }) {
  return (
    <div data-testid="apartments-view">
      <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-3">
        Off-campus student housing · set any as your start point on Today
      </div>
      {!hasData ? (
        <div className={EMPTY}>No housing added for this campus yet. Admins can add complexes in the Campus Manager.</div>
      ) : (
        <div className={GRID}>
          {housing.map((h) => (
            <motion.div key={h.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-border rounded-xl p-5 relative" data-testid={`housing-${h.id}`}>
              <button data-testid={`housing-flag-${h.id}`} onClick={() => openReport("incorrect", h)}
                title="Report incorrect info" className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                <Flag size={14} strokeWidth={1.8} />
              </button>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-md bg-brand/10 text-brand flex items-center justify-center">
                  <Building size={16} strokeWidth={1.5} />
                </div>
                <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{h.type || "apartment"}</span>
                {h.verified === false && (
                  <span data-testid={`housing-unverified-${h.id}`}
                    title="Community-added — pending admin verification"
                    className="mono text-[11px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                    Unverified
                  </span>
                )}
              </div>
              <h3 className="font-display text-lg font-semibold leading-tight pr-6">{h.name}</h3>
              {h.address && (
                <div className="text-sm text-muted-foreground mt-2 flex items-start gap-1.5">
                  <MapPin size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" /> {h.address}
                </div>
              )}
              <DistanceRow est={h._est} testid={`housing-dist-${h.id}`} />
            </motion.div>
          ))}
        </div>
      )}
      <button data-testid="report-missing-housing" onClick={() => openReport("missing", null)}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
        <Plus size={14} strokeWidth={2.5} /> Report a missing or incorrect complex
      </button>
    </div>
  );
}

export function GreekView({ greek, greekType, setGreekType, DistanceRow }) {
  return (
    <div data-testid="greek-view">
      <div className="flex items-center gap-2 mb-4">
        {[
          { k: "all", label: "All" },
          { k: "fraternity", label: "Fraternities" },
          { k: "sorority", label: "Sororities" },
        ].map((f) => (
          <button key={f.k} data-testid={`greek-filter-${f.k}`} onClick={() => setGreekType(f.k)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${greekType === f.k ? "bg-brand text-white border-brand" : "bg-white border-border text-muted-foreground hover:border-brand/40"}`}>
            {f.label}
          </button>
        ))}
      </div>
      {greek.length === 0 ? (
        <div className={EMPTY} data-testid="greek-empty">
          {greekType === "fraternity"
            ? "No fraternity houses listed for this campus yet."
            : greekType === "sorority"
            ? "No sorority houses listed for this campus yet."
            : "No on-campus fraternity or sorority houses added for this campus yet."}
        </div>
      ) : (
        <div className={GRID}>
          {greek.map((g) => (
            <motion.div key={g.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-border rounded-xl p-5" data-testid={`greek-${g.id}`}>
              <div className="flex items-center gap-2.5 mb-1">
                <div data-testid={`greek-badge-${g.id}`}
                  className="w-11 h-11 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0 shadow-sm"
                  style={{ background: g.colors?.bg || "#500000", color: g.colors?.fg || "#ffffff" }}>
                  {g.letters}
                </div>
                <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{g.type}{g.council ? ` · ${g.council}` : ""}</span>
              </div>
              <h3 className="font-display text-lg font-semibold leading-tight">{g.name}</h3>
              {g.address && (
                <div className="text-sm text-muted-foreground mt-2 flex items-start gap-1.5">
                  <MapPin size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" /> {g.address}
                </div>
              )}
              <DistanceRow est={g._est} testid={`greek-dist-${g.id}`} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
